import { createSignal, For, Match, Switch, useContext } from 'solid-js';
import { Heading } from '../../ui-components/heading/heading';
import { Modal } from '../../ui-components/modal/modal';
import clsx from 'clsx';
import { Link } from '../../ui-components/link/link';
import { VideoData } from '../videos/videos-types';
import { AngleLeftIcon } from '../icons-library/angle-left';
import { AngleRightIcon } from '../icons-library/angle-right';
import theme from '../../style/theme';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { FlexRow } from '../../ui-components/utility-style-components/flex';
import { ImageWordpress } from '../../types/shared';
import { VideoIframeContainer } from '../../ui-components/video-iframe-container/video-iframe-container';

type ModalVideoData ={
    selectedPostSlug?: string;
    text?: string;
    heading?: string;
    image?: ImageWordpress;
    video?: VideoData;
}

type VideoModalProps = {
    show: boolean;
    closeModal: () => void;
    video?: {
        videoLink?: string;
    };
    modalVideoTitle?: string;
    modalLink?: {
        title: string;
        url: string;
    };
    videos?: ModalVideoData[];
    initialVideoIndex?: number;
    closeButtonRef: (el?: any) => void;
    setLastTabbableElement: (el?: any) => void;
};

export const VideoModal = (props: VideoModalProps) => {
    const { viewport, localize } = useContext(AppContext);

    const [ currentVideoIndex, setCurrentVideoIndex ] = createSignal(props.initialVideoIndex || 0);

    const isUnderTablet = () => viewport.width <= theme.breakpoints.ORIGINAL_TABLET;

    
    const previousVideo = () => () => {
        if (currentVideoIndex() > 0) {
            setCurrentVideoIndex(currentVideoIndex() - 1);
            return;
        }
        setCurrentVideoIndex(props.videos!.length - 1);
    };

    const nextVideo = () => () => {
        if (currentVideoIndex() < (props.videos!.length - 1)) {
            setCurrentVideoIndex(currentVideoIndex() + 1);
            return;
        }
        setCurrentVideoIndex(0);
    };

    const currentVideo = () => {
        if (props.videos) {
            return props.videos[currentVideoIndex()];
        }
    };

    const videoComponentForMultipleVideos = () => {
        return (
            <div style={{ height: 'auto', width: '100%' }}>
                <VideoIframeContainer videoLink={currentVideo()?.video?.video_link} />
            </div>
        );
    };

    return (
        <Modal 
            visible={props.show}
            onClose={props.closeModal}
            size='large'
            darkenBackground={true}
            closeButtonRef={(el: HTMLElement) => props.closeButtonRef && props.closeButtonRef(el)}
        >
            <div class={clsx(
                'flex flex-col items-center',
                !props.videos?.length && 'px-[3rem]',
                'max-[600px]:px-0 max-[600px]:pt-[1rem]'
            )}>
                <Switch>

                    <Match when={props.video}>
                        <Heading tag="h5" variant='medium' noBlockSpacing={true}>
                            {props.modalVideoTitle}
                        </Heading>

                        <div style={{ height: 'auto', width: '100%' }}>
                            <VideoIframeContainer videoLink={props?.video?.videoLink} />
                        </div>

                        <Link
                            label={props.modalLink?.title || ''}
                            url={props.modalLink?.url || ''}
                            noBlockSpacing={true}
                            element={(el: any) => {
                                props.setLastTabbableElement(el);
                            }}
                        />
                    </Match>

                    <Match when={props.videos}>
                        <Heading tag="h5" variant='medium' noBlockSpacing={true}>
                            {props!.videos![currentVideoIndex()].heading}
                        </Heading>

                        <Switch>
                            <Match when={!isUnderTablet()}>
                                <div class="flex w-full justify-between gap-[3rem]">
                                    <button 
                                        onClick={previousVideo()}
                                        aria-label={localize('slider-previous', 'Previous')}
                                    >
                                        <AngleLeftIcon fill={theme.palette.gray} />
                                    </button>

                                    {videoComponentForMultipleVideos()}

                                    <button
                                        onClick={nextVideo()}
                                        aria-label={localize('slider-next', 'Next')}
                                    >
                                        <AngleRightIcon fill={theme.palette.gray} />
                                    </button>
                                </div>

                                <div class="flex justify-center my-[1rem] gap-[0.4rem]">
                                    <For each={props.videos}>{(video: ModalVideoData, index) => (
                                        <button
                                            class={clsx(
                                                'w-[0.9rem] h-[0.9rem] rounded-half mx-[0.5rem] cursor-pointer',
                                                currentVideoIndex() === index() ? 'bg-darkGray' : 'bg-gray-light'
                                            )}
                                            onClick={() => setCurrentVideoIndex(index)}
                                            aria-label={localize('open-video', 'Open Video ' + (index() + 1))}
                                            ref={(el: any) => {
                                                if (index() === props.videos!.length - 1) {
                                                    props.setLastTabbableElement(el);
                                                }
                                            }}
                                        />
                                    )}</For>
                                </div>
                            </Match>

                            <Match when={isUnderTablet()}>
                                {videoComponentForMultipleVideos()}

                                <FlexRow gap="2rem">
                                    <button
                                        onClick={previousVideo()}
                                        aria-label={localize('slider-previous', 'Previous')}
                                    >
                                        <AngleLeftIcon fill={theme.palette.gray} />
                                    </button>

                                    <div class="flex justify-center my-[1rem] gap-[0.4rem]">
                                        <For each={props.videos}>{(video: ModalVideoData, index) => (
                                            <button
                                                class={clsx(
                                                    'w-[0.9rem] h-[0.9rem] rounded-half mx-[0.5rem] cursor-pointer',
                                                    currentVideoIndex() === index() ? 'bg-darkGray' : 'bg-gray-light'
                                                )}
                                                onClick={() => setCurrentVideoIndex(index)}
                                                aria-label={localize('open-video', 'Open Video ' + (index() + 1))}
                                            />
                                        )}</For>
                                    </div>

                                    <button
                                        onClick={nextVideo()}
                                        aria-label={localize('slider-next', 'Next')}
                                    >
                                        <AngleRightIcon fill={theme.palette.gray} />
                                    </button>
                                </FlexRow>
                            </Match>
                        </Switch>
                    </Match>
                </Switch>
            </div>
        </Modal>
    );
};

